import { Check, X, Minus, Star, Quote } from 'lucide-react';
import { Container, Section, Overline, H2, Lead, FadeUp } from './shared';
import { AI_FEATURES, INTEGRATIONS, SECURITY, TESTIMONIALS, COMPARISON } from './landingData';

export const AISection = () => (
  <Section testId="ai-features-section" className="bg-[#0B0C0E] overflow-hidden">
    <div className="absolute inset-0 ld-spot-cyan" />
    <Container className="relative grid lg:grid-cols-12 gap-12 items-start">
      <FadeUp className="lg:col-span-5">
        <Overline>Intelligence layer</Overline>
        <H2>An AI operations analyst that never sleeps.</H2>
        <Lead className="mt-4">Powered by Claude by Anthropic. It reads every report, complaint and deal — then tells each person exactly what to do next.</Lead>
        <div className="mt-8 ld-glass rounded-2xl p-5 text-sm" data-testid="ai-briefing-sample">
          <p className="ld-mono text-[10px] tracking-[0.2em] text-slate-500 mb-3">MORNING BRIEFING · 09:00</p>
          <p className="text-slate-200 leading-relaxed">Good morning, Rajesh. <span className="text-[#E06D53]">3 complaints</span> breach SLA in 4 hours. <span className="text-emerald-400">BuildMax deal (₹18L)</span> has gone quiet for 6 days — a follow-up draft is ready. 94% of the team submitted yesterday's report.</p>
        </div>
      </FadeUp>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {AI_FEATURES.map((f, i) => (
          <FadeUp key={f.title} delay={i * 0.08}>
            <div className="ld-card ld-card-lift p-6 h-full" data-testid={`ai-feature-${i}`}>
              <f.icon className="w-5 h-5 text-[#38BDF8]" />
              <h3 className="text-base font-semibold text-slate-100 mt-4">{f.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </Container>
  </Section>
);

export const Integrations = () => (
  <section className="py-16 border-y border-white/[0.06]" data-testid="integrations-section">
    <Container className="flex flex-col lg:flex-row lg:items-center gap-8">
      <div className="lg:w-72 shrink-0">
        <Overline className="!mb-2">Integrations</Overline>
        <p className="text-slate-300 text-sm leading-relaxed">Plugs into the tools your team already lives in.</p>
      </div>
      <div className="flex flex-wrap gap-2.5" data-testid="integration-logos">
        {INTEGRATIONS.map((n) => <span key={n} className="ld-badge !text-xs !py-2 !px-4 hover:border-white/25 hover:text-slate-200 transition-colors">{n}</span>)}
      </div>
    </Container>
  </section>
);

export const Security = () => (
  <Section id="security" testId="security-section">
    <Container>
      <FadeUp className="max-w-2xl">
        <Overline>Security & compliance</Overline>
        <H2>Enterprise-grade by default. Not an add-on.</H2>
        <Lead className="mt-4">Your operations data is the most sensitive thing your company owns. We treat it that way.</Lead>
      </FadeUp>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden mt-14">
        {SECURITY.map((s, i) => (
          <FadeUp key={s.title} delay={i * 0.06} className="bg-[#08090A] hover:bg-[#0F1013] transition-colors p-6 sm:p-8">
            <div data-testid={`security-item-${i}`}>
              <s.icon className="w-5 h-5 text-emerald-400" />
              <h3 className="text-base font-semibold text-slate-100 mt-4">{s.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{s.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </Container>
  </Section>
);

export const Testimonials = () => (
  <Section id="testimonials" testId="testimonials-section" className="bg-[#0B0C0E]">
    <Container>
      <FadeUp className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="max-w-2xl">
          <Overline>Customers</Overline>
          <H2>Operations leaders who stopped chasing updates.</H2>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="testimonial-rating">
          <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#E06D53] text-[#E06D53]" />)}</span>
          <span className="ld-mono">4.9 / 5</span><span className="text-slate-500">· 180+ reviews</span>
        </div>
      </FadeUp>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {TESTIMONIALS.map((t, i) => (
          <FadeUp key={t.name} delay={(i % 3) * 0.08}>
            <figure className="ld-card p-6 sm:p-7 h-full flex flex-col" data-testid={`testimonial-${i}`}>
              <Quote className="w-5 h-5 text-[#E06D53]/60" />
              <blockquote className="text-slate-200 leading-relaxed mt-4 flex-1">"{t.quote}"</blockquote>
              <figcaption className="flex items-center gap-3 mt-6 pt-5 border-t border-white/[0.06]">
                <img src={t.avatar} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10" loading="lazy" />
                <div><p className="text-sm font-medium text-slate-100">{t.name}</p><p className="text-xs text-slate-500">{t.role}</p></div>
              </figcaption>
            </figure>
          </FadeUp>
        ))}
      </div>
    </Container>
  </Section>
);

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-4 h-4 text-emerald-400" />;
  if (v === false) return <X className="w-4 h-4 text-slate-600" />;
  if (v === 'partial') return <Minus className="w-4 h-4 text-amber-400" />;
  return <span className="ld-mono text-slate-200">{v}</span>;
};

export const Comparison = () => (
  <Section testId="comparison-section">
    <Container>
      <FadeUp className="max-w-2xl">
        <Overline>Comparison</Overline>
        <H2>Why teams switch from spreadsheets and siloed apps.</H2>
      </FadeUp>
      <FadeUp delay={0.1} className="mt-12 ld-card overflow-x-auto">
        <table className="w-full ld-table min-w-[560px]" data-testid="comparison-table">
          <thead><tr><th>Capability</th><th className="ld-hl text-[#E06D53] !font-bold">Woxora</th><th>Spreadsheets + WhatsApp</th><th>Siloed apps</th></tr></thead>
          <tbody>
            {COMPARISON.map((r) => (
              <tr key={r.name}><td className="text-slate-300">{r.name}</td><td className="ld-hl"><Cell v={r.woxora} /></td><td><Cell v={r.sheets} /></td><td><Cell v={r.siloed} /></td></tr>
            ))}
          </tbody>
        </table>
      </FadeUp>
    </Container>
  </Section>
);
